import React, { useState, useEffect } from 'react';
import {
  User, KeyRound, Shield, Save, AlertCircle,
  CheckCircle2, Eye, EyeOff, Mail, Calendar
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { validatePassword, sanitizeInput, validateFullName } from '../utils/validation';
import '../styles/settings.css';

const SettingsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  // Profile state
  const [fullName, setFullName] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (user) setFullName(user.user_metadata?.full_name || '');
  }, [user]);

  // ── Update profile name ──────────────────────────────────────────
  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess(false);

    const clean = sanitizeInput(fullName);
    if (!validateFullName(clean)) {
      setProfileError('Please enter a valid full name (letters only, 2–60 characters).');
      return;
    }

    setProfileLoading(true);
    const { error: authError } = await supabase.auth.updateUser({
      data: { full_name: clean }
    });

    if (!authError) {
      await supabase
        .from('profiles')
        .update({ full_name: clean, updated_at: new Date().toISOString() })
        .eq('id', user.id);
    }

    setProfileLoading(false);
    if (authError) {
      setProfileError('Failed to update profile. Please try again.');
    } else {
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    }
  };

  // ── Change password ──────────────────────────────────────────────
  // ROOT CAUSE OF 400 ERROR:
  // Calling signInWithPassword() while already logged in causes a
  // session conflict in Supabase, returning a 400 error.
  // FIX: We use supabase.auth.updateUser() directly. Supabase enforces
  // the "Secure password change" setting we enabled, which requires
  // the user to have signed in recently. We verify the current password
  // by signing into a temporary check without disturbing the session.
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    // Client-side validation first
    const pwdErrors = validatePassword(newPassword);
    if (pwdErrors.length > 0) {
      setPasswordError('New password does not meet the requirements below.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from your current password.');
      return;
    }
    if (!currentPassword) {
      setPasswordError('Please enter your current password.');
      return;
    }

    setPasswordLoading(true);

    try {
      // CORRECT FIX FOR SUPABASE JS v2.39+:
      // The "nonce" parameter was removed in newer Supabase JS versions.
      // The "Require current password when updating" Supabase setting must be
      // turned OFF in the dashboard — we handle current password verification
      // ourselves via signInWithPassword BEFORE calling updateUser.
      // The key difference from the previous broken approach: we sign in first
      // to get a FRESH SESSION TOKEN, then immediately call updateUser in the
      // same async flow. This satisfies Supabase's session freshness requirement.

      // Step 1 — Verify current password and refresh the session token
      const { data: signInData, error: signInError } =
        await supabase.auth.signInWithPassword({
          email: user.email,
          password: currentPassword,
        });

      if (signInError || !signInData?.session) {
        setPasswordError('Current password is incorrect. Please try again.');
        setPasswordLoading(false);
        return;
      }

      // Step 2 — Use the fresh session to update the password immediately
      // We call updateUser on the same supabase instance which now holds
      // the freshly issued session token from the signIn above
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        if (updateError.message?.toLowerCase().includes('same password') ||
            updateError.message?.toLowerCase().includes('different') ||
            updateError.code === 'same_password') {
          setPasswordError('New password must be different from your current password.');
        } else if (updateError.message?.toLowerCase().includes('weak')) {
          setPasswordError('Password is too weak. Please choose a stronger password.');
        } else {
          setPasswordError('Failed to update password. Please try again.');
        }
        setPasswordLoading(false);
        return;
      }

      // Success — clear all fields
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordErrors([]);
      setTimeout(() => setPasswordSuccess(false), 5000);

    } catch (err) {
      console.error('Password change error:', err);
      setPasswordError('An unexpected error occurred. Please try again.');
    }

    setPasswordLoading(false);
  };

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      })
    : 'N/A';

  const initials = (user?.user_metadata?.full_name || user?.email || 'U')
    .split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1 className="settings-title">Account Settings</h1>
        <p className="settings-subtitle">Manage your profile and security preferences</p>
      </div>

      <div className="settings-layout">
        {/* Sidebar */}
        <div className="settings-sidebar">
          <div className="settings-user-card">
            <div className="settings-avatar">{initials}</div>
            <p className="settings-user-name">{user?.user_metadata?.full_name || 'User'}</p>
            <p className="settings-user-email">{user?.email}</p>
            <div className="settings-member-since">
              <Calendar size={12} />
              Member since {memberSince}
            </div>
          </div>

          <nav className="settings-nav">
            {[
              { id: 'profile', icon: User, label: 'Profile' },
              { id: 'password', icon: KeyRound, label: 'Password' },
              { id: 'security', icon: Shield, label: 'Security Info' },
            ].map(({ id, icon: Icon, label }) => (
              <button key={id}
                className={`settings-nav-item${activeTab === id ? ' settings-nav-active' : ''}`}
                onClick={() => setActiveTab(id)}>
                <Icon size={16} />{label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="settings-content">

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="settings-card">
              <div className="settings-card-header">
                <User size={18} color="#E8471E" />
                <h2 className="settings-card-title">Profile Information</h2>
              </div>

              {profileError && (
                <div className="settings-alert settings-alert-error">
                  <AlertCircle size={15} />{profileError}
                </div>
              )}
              {profileSuccess && (
                <div className="settings-alert settings-alert-success">
                  <CheckCircle2 size={15} />Profile updated successfully!
                </div>
              )}

              <form onSubmit={handleProfileSave}>
                <div className="form-group">
                  <label className="form-label">Full name</label>
                  <input type="text" className="form-input"
                    value={fullName} onChange={e => setFullName(e.target.value)}
                    placeholder="Your full name" maxLength={60} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email address</label>
                  <div className="input-disabled-wrapper">
                    <Mail size={15} className="input-prefix-icon" />
                    <input type="email" className="form-input form-input-disabled"
                      value={user?.email || ''} disabled />
                  </div>
                  <p className="form-hint">Email address cannot be changed for security reasons.</p>
                </div>
                <button type="submit"
                  className={`btn-primary btn-primary-sm${profileLoading ? ' btn-loading' : ''}`}
                  disabled={profileLoading}>
                  {profileLoading ? <span className="btn-spinner" /> : <><Save size={16} />Save changes</>}
                </button>
              </form>
            </div>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <div className="settings-card">
              <div className="settings-card-header">
                <KeyRound size={18} color="#E8471E" />
                <h2 className="settings-card-title">Change Password</h2>
              </div>

              {passwordError && (
                <div className="settings-alert settings-alert-error">
                  <AlertCircle size={15} />{passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="settings-alert settings-alert-success">
                  <CheckCircle2 size={15} />Password changed successfully! Use your new password next time you log in.
                </div>
              )}

              <form onSubmit={handlePasswordChange}>
                <div className="form-group">
                  <label className="form-label">Current password</label>
                  <div className="input-with-icon">
                    <input
                      type={showCurrent ? 'text' : 'password'}
                      className="form-input"
                      placeholder="Enter your current password"
                      value={currentPassword}
                      onChange={e => setCurrentPassword(e.target.value)}
                      required maxLength={128}
                      autoComplete="current-password"
                    />
                    <button type="button" className="input-icon-btn"
                      onClick={() => setShowCurrent(v => !v)}>
                      {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">New password</label>
                  <div className="input-with-icon">
                    <input
                      type={showNew ? 'text' : 'password'}
                      className="form-input"
                      placeholder="Create a strong new password"
                      value={newPassword}
                      onChange={e => {
                        setNewPassword(e.target.value);
                        setPasswordErrors(validatePassword(e.target.value));
                      }}
                      required maxLength={128}
                      autoComplete="new-password"
                    />
                    <button type="button" className="input-icon-btn"
                      onClick={() => setShowNew(v => !v)}>
                      {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {newPassword && (
                    <ul className="password-hints">
                      {[
                        'At least 8 characters',
                        'At least one uppercase letter',
                        'At least one number',
                        'At least one special character (!@#$%...)'
                      ].map(req => (
                        <li key={req} className={passwordErrors.includes(req) ? 'hint-fail' : 'hint-pass'}>
                          {passwordErrors.includes(req) ? '✗' : '✓'} {req}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Confirm new password</label>
                  <div className="input-with-icon">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      className="form-input"
                      placeholder="Re-enter your new password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      required maxLength={128}
                      autoComplete="new-password"
                    />
                    <button type="button" className="input-icon-btn"
                      onClick={() => setShowConfirm(v => !v)}>
                      {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button type="submit"
                  className={`btn-primary btn-primary-sm${passwordLoading ? ' btn-loading' : ''}`}
                  disabled={passwordLoading}>
                  {passwordLoading ? <span className="btn-spinner" /> : <><KeyRound size={16} />Update password</>}
                </button>
              </form>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="settings-card">
              <div className="settings-card-header">
                <Shield size={18} color="#E8471E" />
                <h2 className="settings-card-title">Security Information</h2>
              </div>

              <div className="security-info-list">
                {[
                  { label: 'Account email', value: user?.email, icon: Mail },
                  { label: 'Email verified', value: user?.email_confirmed_at ? '✓ Verified' : '✗ Not verified', icon: CheckCircle2 },
                  { label: 'Last sign in', value: user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString() : 'N/A', icon: Calendar },
                  { label: 'Account created', value: memberSince, icon: Calendar },
                  { label: 'Authentication method', value: 'Email & Password', icon: Shield },
                ].map(({ label, value, icon: Icon }) => (
                  <div key={label} className="security-info-row">
                    <div className="security-info-label"><Icon size={14} />{label}</div>
                    <div className="security-info-value">{value}</div>
                  </div>
                ))}
              </div>

              <div className="security-tips">
                <h3 className="security-tips-title">🔐 Security Tips</h3>
                <ul className="security-tips-list">
                  <li>Use a unique password not used on other websites</li>
                  <li>Never share your password with anyone including SynFin staff</li>
                  <li>Log out when using shared or public computers</li>
                  <li>Contact support immediately if you notice suspicious activity</li>
                </ul>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
